import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
    Badge,
    Button,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    TableFooter,
    TableToolbar,
    Text,
} from "@/ui";
import { useNotification } from "@/hooks";
import Content from "../../base/layout/content/Content";
import useMeta from "../../base/hooks/useMeta.js";
import {
    QUERY_ACCOUNT_LIST,
    MUTATION_REMOVE_ACCOUNT,
} from "../../apollo/module-operations/business_account";
import routes from "../../config/routes/routes";
import moment from "moment";
import _ from "lodash";
import { formatAddress } from "../../utils/utils";
import { PlusIcon, RowActionsMenu, storeTableUiStyles } from "../store/shared";
import styles from "../store/GeneralsForm.module.scss";
import deliveryStyles from "../store/DeliverySchedules.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const BusinessAccounts = () => {
    const { user } = useMeta();
    const history = useHistory();
    const notify = useNotification();

    const [search, setSearch] = useState("");
    const [companyFilter, setCompanyFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { loading, data, refetch, networkStatus } = useQuery(
        QUERY_ACCOUNT_LIST,
        {
            skip: user.loading || !user.accountId || !user.storeId,
            notifyOnNetworkStatusChange: true,
        },
    );

    const [removeAccount] = useMutation(MUTATION_REMOVE_ACCOUNT, {
        refetchQueries: [
            {
                query: QUERY_ACCOUNT_LIST,
            },
        ],
        onCompleted: () => {
            setIsDeleting(false);
            setAccountToDelete(null);
            notify("Account Was Removed Successfully.", "success");
        },
        onError: () => {
            setIsDeleting(false);
            notify(
                "Error Removing The Account, If The Error Persist Please Contact Support.",
                "error",
            );
        },
    });

    const update = id => {
        history.push(
            _.replace(routes.BUSINESS_ACCOUNT_DETAIL.route, ":id", id),
        );
    };

    const confirmDelete = () => {
        if (!accountToDelete) return;
        setIsDeleting(true);
        removeAccount({
            variables: {
                id: accountToDelete.id,
            },
        });
    };

    const accounts = useMemo(
        () => (data && data.allBusinessAccounts) || [],
        [data],
    );

    const companyOptions = useMemo(() => {
        const values = Array.from(
            new Set(
                accounts.map(row => (row.company || "").trim()).filter(Boolean),
            ),
        ).sort((a, b) => a.localeCompare(b));
        return values;
    }, [accounts]);

    const clearFilters = useCallback(() => {
        setCompanyFilter(null);
        setStatusFilter(null);
        setSearch("");
        setPage(1);
    }, []);

    const companyMenuItems = useMemo(
        () => [
            {
                id: "all-companies",
                label: "All companies",
                onSelect: () => {
                    setCompanyFilter(null);
                    setPage(1);
                },
            },
            ...companyOptions.map(company => ({
                id: `company-${company}`,
                label: company,
                onSelect: () => {
                    setCompanyFilter(company);
                    setPage(1);
                },
            })),
        ],
        [companyOptions],
    );

    const statusMenuItems = useMemo(
        () => [
            {
                id: "status-active",
                label: "Active",
                onSelect: () => {
                    setStatusFilter(true);
                    setPage(1);
                },
            },
            {
                id: "status-inactive",
                label: "No Active",
                onSelect: () => {
                    setStatusFilter(false);
                    setPage(1);
                },
            },
        ],
        [],
    );

    const toolbarFilters = useMemo(
        () => [
            {
                id: "f-company",
                label: companyFilter ? `Company · ${companyFilter}` : "Company",
                active: Boolean(companyFilter),
                menuItems: companyFilter ? undefined : companyMenuItems,
                onRemove: companyFilter
                    ? () => {
                          setCompanyFilter(null);
                          setPage(1);
                      }
                    : undefined,
            },
            {
                id: "f-status",
                label:
                    statusFilter === null
                        ? "Status"
                        : `Status · ${statusFilter ? "Active" : "No Active"}`,
                active: statusFilter !== null,
                menuItems: statusFilter !== null ? undefined : statusMenuItems,
                onRemove:
                    statusFilter !== null
                        ? () => {
                              setStatusFilter(null);
                              setPage(1);
                          }
                        : undefined,
            },
        ],
        [companyFilter, statusFilter, companyMenuItems, statusMenuItems],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        const rows = accounts.filter(row => {
            if (companyFilter && row.company !== companyFilter) {
                return false;
            }
            if (statusFilter !== null && Boolean(row.status) !== statusFilter) {
                return false;
            }
            if (!q) return true;

            const address = row.address ? formatAddress(row.address) : "";
            const created = row.created_at
                ? moment(row.created_at).format("MM/DD/YYYY HH:mm")
                : "";
            const haystack = [
                row.id,
                row.company,
                row.first_name,
                row.last_name,
                row.phone,
                address,
                created,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(q);
        });

        return [...rows].sort((a, b) => {
            const aTime = a.created_at ? moment(a.created_at).valueOf() : 0;
            const bTime = b.created_at ? moment(b.created_at).valueOf() : 0;
            return bTime - aTime;
        });
    }, [accounts, search, companyFilter, statusFilter]);

    const hasActiveFilters =
        Boolean(search.trim()) ||
        Boolean(companyFilter) ||
        statusFilter !== null;

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageRows = useMemo(
        () => filtered.slice(pageStart, pageStart + pageSize),
        [filtered, pageStart, pageSize],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            label={row.company || row.id || "account"}
                            onDelete={() => setAccountToDelete(row)}
                            onUpdate={() => update(row.id)}
                        />
                    </span>
                ),
            },
            {
                id: "id",
                header: "Id",
                minWidth: 180,
                renderCell: row => row.id || "",
            },
            {
                id: "company",
                header: "Company",
                minWidth: 120,
                renderCell: row => row.company || "",
            },
            {
                id: "created_at",
                header: "Created At",
                minWidth: 140,
                renderCell: row =>
                    row.created_at
                        ? moment(row.created_at).format("MM/DD/YYYY HH:mm")
                        : "",
            },
            {
                id: "first_name",
                header: "First Name",
                minWidth: 110,
                renderCell: row => row.first_name || "",
            },
            {
                id: "last_name",
                header: "Last Name",
                minWidth: 110,
                renderCell: row => row.last_name || "",
            },
            {
                id: "phone",
                header: "Phone",
                minWidth: 110,
                renderCell: row => row.phone || "",
            },
            {
                id: "address",
                header: "Address",
                minWidth: 220,
                renderCell: row =>
                    row.address ? formatAddress(row.address) : "",
            },
            {
                id: "status",
                header: "Status",
                minWidth: 100,
                renderCell: row => (
                    <Badge variant={row.status ? "primary" : "neutral"}>
                        {row.status ? "Active" : "No Active"}
                    </Badge>
                ),
            },
        ],
        [],
    );

    const _loading = loading || networkStatus === 4;

    const headerActions = [
        {
            id: "create",
            label: "Add New",
            icon: <PlusIcon />,
            onClick: () => {
                history.push(routes.BUSINESS_ACCOUNT_CREATE.route);
            },
        },
    ];

    return (
        <>
            <Modal
                description="Are You Sure You Want To Remove The Account ?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isDeleting}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isDeleting}
                            onClick={() => setAccountToDelete(null)}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) setAccountToDelete(null);
                }}
                open={Boolean(accountToDelete)}
                title="Remove account"
            >
                {null}
            </Modal>

            <Content>
                <Panel
                    className={styles.root}
                    header={
                        <div className={styles.headerBar}>
                            <div className={styles.headerText}>
                                <Heading level={4}>Accounts</Heading>
                                <Text tone="secondary" variant="body-md">
                                    All accounts availables in the system.
                                </Text>
                            </div>
                            <HeaderActions
                                actions={headerActions}
                                aria-label="Accounts actions"
                            />
                        </div>
                    }
                    showHeaderDivider
                >
                    {_loading ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading"
                            />
                        </div>
                    ) : (
                        <>
                            <TableToolbar
                                count={filtered.length}
                                filters={toolbarFilters}
                                label="Accounts"
                                mode={
                                    hasActiveFilters
                                        ? "active-filters"
                                        : "filterable"
                                }
                                onClearAllFilters={clearFilters}
                                onRefresh={() => refetch()}
                                onSearchChange={value => {
                                    setSearch(value);
                                    setPage(1);
                                }}
                                searchable
                                searchValue={search}
                                showMoreFilters={false}
                                updatedAtLabel=""
                            />
                            <Table
                                columns={columns}
                                getRowId={row => String(row.id)}
                                rows={pageRows}
                            />
                            <TableFooter
                                onPageChange={setPage}
                                onPageSizeChange={size => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                                page={currentPage}
                                pageSize={pageSize}
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                totalPages={totalPages}
                                totalRows={filtered.length}
                            />
                        </>
                    )}
                </Panel>
            </Content>
        </>
    );
};

export default BusinessAccounts;
